/**
 * Per-category ESTIMATION TAXONOMY profile - the vocabulary an estimation category runs on:
 * which work types exist (with definitions the prompts and UI share), which of them are
 * delivery PHASES of a build (and how the estimator prompt names them), the PM synthesis
 * model, and (optionally) the firm's canonical business-process workstreams.
 *
 * Stored as `deal_engine_profile` rows with profile_type='taxonomy' (system rows seed-owned,
 * workspace rows customer-owned via the category clone chain), resolved category-first by
 * `loadTaxonomyProfile` (apps/api estimate/config.ts) and threaded into the AI runtime via the
 * `EstimationTaxonomyPort` on AppServices. ABSENT PROFILE == UNCHANGED BEHAVIOR:
 * {@link DEFAULT_ESTIMATION_TAXONOMY} is derived from {@link WORK_TYPE_OPTIONS}, so an
 * unseeded database renders the same prompt vocabulary as the canonical defaults.
 *
 * WorkType remains the canonical UNIVERSAL superset (see scope.ts): a profile SELECTS a subset,
 * may OVERRIDE labels/definitions, and may EXTEND with slug-keyed custom types (which must carry
 * their own label + definition). Free-string vocabularies are deliberately rejected - the
 * flywheel dimension keys, corpus topology matching, and role-affinity seeding all depend on
 * cross-workspace comparability of the canonical keys.
 */
import { z } from 'zod';
import { WORK_TYPE_OPTIONS } from './scope.js';
/** A canonical WorkType key or a profile-declared slug extension. */
const workTypeKeySchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{2,32}$/, 'work-type keys are lowercase slugs (3-33 chars)');
/** One work type the category estimates in: a canonical key (label/definition optional -
 *  defaults from WORK_TYPE_OPTIONS) or a slug extension (label + definition required). */
export const taxonomyWorkTypeSchema = z.object({
    key: workTypeKeySchema,
    label: z.string().min(1).optional(),
    definition: z.string().min(1).optional(),
});
/** A work type that is a delivery PHASE of a build (not a standalone deliverable) + how the
 *  estimator prompt names it and the example component label it should surface. */
export const taxonomyDeliveryPhaseSchema = z.object({
    workType: workTypeKeySchema,
    /** How the phase reads in the estimator prompt, e.g. 'testing (QA/UAT)'. */
    promptLabel: z.string().min(1),
    /** The example component name the prompt suggests, e.g. 'UAT & defect fixes'. */
    componentExample: z.string().min(1),
});
/** How PM hours are synthesized for AI-mode estimates in this category.
 *  'percent_of_scope' (today's behavior; percent null => the calculator config's pm_percent),
 *  'none' => the category's rule engine owns PM (prevents double-count in hybrid categories). */
export const taxonomyPmModelSchema = z.object({
    kind: z.enum(['percent_of_scope', 'none']),
    percent: z.number().min(0).max(1).nullable().optional(),
    roleId: z.string().nullable().optional(),
    sectionLabel: z.string().min(1).optional(),
});
const sowTokenNameSchema = z.string().min(1);
/**
 * Declarative SOW context-token binding - the taxonomy-profile replacement for a product
 * config's hand-written `buildContextTokens`. The source union is derived from exactly what
 * the shipped per-product builders (NetSuite + Rillet) produce today, nothing speculative:
 *   - 'client_name'           the shared client-name resolver (priority field keys plus the
 *                             company_overview JSON fallback) - CLIENT_NAME / CLIENT_LEGAL_NAME.
 *   - 'migration_option'      the shared migration-option label resolver - MIGRATION_OPTION.
 *   - 'field'                 first non-empty questionnaire field among `fieldKeys`; when empty,
 *                             the `fallback` literal, else the SOW generation date when
 *                             `fallbackSource: 'generated_date'`, else '' - GO_LIVE_DATE
 *                             (fallback 'TBD'), EFFECTIVE_DATE (start_date || generation date).
 *   - 'static'                fixed text - PROJECT_NAME.
 *   - 'month_year'            "January 2026" from the generation date - MONTH_YEAR.
 *   - 'expiration_from_field' the SOW expiration date computed from a go-live field -
 *                             SOW_EXPIRATION_DATE.
 * Estimate-hours tokens are NOT modeled here - they remain the product config's
 * buildHoursTokens concern.
 */
export const taxonomySowTokenSchema = z.discriminatedUnion('source', [
    z.object({
        token: sowTokenNameSchema,
        source: z.literal('field'),
        fieldKeys: z.array(z.string().min(1)).min(1),
        fallback: z.string().optional(),
        fallbackSource: z.enum(['generated_date']).optional(),
    }),
    z.object({ token: sowTokenNameSchema, source: z.literal('static'), value: z.string() }),
    z.object({ token: sowTokenNameSchema, source: z.literal('client_name') }),
    z.object({ token: sowTokenNameSchema, source: z.literal('migration_option') }),
    z.object({ token: sowTokenNameSchema, source: z.literal('month_year') }),
    z.object({
        token: sowTokenNameSchema,
        source: z.literal('expiration_from_field'),
        fieldKeys: z.array(z.string().min(1)).min(1),
    }),
]);
/** A taxonomy's declarative SOW context-token set. Absent (the default taxonomy) means the
 *  per-product `buildContextTokens` path runs unchanged - the product config is the fallback. */
export const taxonomySowContextSchema = z.object({
    tokens: z.array(taxonomySowTokenSchema),
});
export const estimationTaxonomySchema = z.object({
    schemaVersion: z.string().default('1.0.0'),
    workTypes: z.array(taxonomyWorkTypeSchema).min(1),
    deliveryPhases: z.array(taxonomyDeliveryPhaseSchema).default([]),
    pmModel: taxonomyPmModelSchema.default({ kind: 'percent_of_scope' }),
    /** Optional canonical business-process workstreams (candidates for the A6 grouping). */
    businessProcesses: z.array(z.string().min(1)).optional(),
    sowContext: taxonomySowContextSchema.optional(),
});
/**
 * The default taxonomy: every canonical work type (definitions from WORK_TYPE_OPTIONS).
 * No delivery phases - testing and deployment/go-live are not standalone activities in the
 * canonical vocabulary; they are part of every build's delivery (the estimator prompt costs
 * them inside the build item's components). A category profile may still declare its own
 * `deliveryPhases` over extension keys. Loader fallback when no profile row resolves.
 */
export const DEFAULT_ESTIMATION_TAXONOMY = {
    schemaVersion: '1.0.0',
    workTypes: WORK_TYPE_OPTIONS.map((o) => ({ key: o.value })),
    deliveryPhases: [],
    pmModel: { kind: 'percent_of_scope', percent: null, roleId: null, sectionLabel: 'Project Management' },
};
/** Parse arbitrary profile config into a taxonomy; null on failure (caller falls back to default). */
export function parseEstimationTaxonomy(input) {
    const r = estimationTaxonomySchema.safeParse(input ?? {});
    return r.success ? r.data : null;
}
const CANON = new Map(WORK_TYPE_OPTIONS.map((o) => [o.value, o]));
/** The taxonomy's work types resolved against the canonical defaults: {key,label,definition}. */
export function taxonomyWorkTypeEntries(t) {
    return t.workTypes.map((wt) => {
        const canon = CANON.get(wt.key);
        return {
            key: wt.key,
            label: wt.label ?? canon?.label ?? wt.key,
            definition: wt.definition ?? canon?.definition ?? wt.label ?? wt.key,
        };
    });
}
/** The taxonomy's work-type keys (validation set for profile-aware edges). */
export function taxonomyWorkTypeKeys(t) {
    return t.workTypes.map((wt) => wt.key);
}
/**
 * The "value = definition" prompt block for THIS taxonomy - the profile-aware replacement for
 * the global workTypeDefinitions() at estimation/extraction call sites. For the default
 * taxonomy this renders byte-identically to workTypeDefinitions().
 */
export function taxonomyWorkTypeDefinitions(t) {
    return taxonomyWorkTypeEntries(t)
        .map((e) => `${e.key} = ${e.definition}`)
        .join('\n');
}
/** 'testing (QA/UAT) or deployment (go-live/cutover)' - the phase list as the estimator prompt
 *  names it. Empty string when the taxonomy declares no phases. */
export function taxonomyPhaseListText(t) {
    return t.deliveryPhases.map((p) => p.promptLabel).join(' or ');
}
/** '"UAT & defect fixes", "Cutover & go-live"' - the example component names. */
export function taxonomyPhaseExamplesText(t) {
    return t.deliveryPhases.map((p) => `"${p.componentExample}"`).join(', ');
}
/** 'testing (QA/UAT) and deployment (go-live/cutover)' - the phase list joined for the HONOR
 *  clause of the estimator prompt. */
export function taxonomyPhaseHonorText(t) {
    return t.deliveryPhases.map((p) => p.promptLabel).join(' and ');
}
//# sourceMappingURL=estimation-taxonomy.js.map